import {
  CURRENT_WILDFIRE_INCIDENTS_URL,
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_ARCGIS_LAYER_SOURCE,
  DEFAULT_SENSOR_ALPHA_SOURCE,
  DEFAULT_WILDFIRE_INCIDENTS_SOURCE,
  isArcGisLayerSourcePreset,
  isSensorAlphaSourcePreset,
  isTropicalSourcePreset,
  type AttributeCondition,
  type OrgEventAutomationCondition,
  type OrgEventAutomationLogic,
  type OrgEventAutomationLogicBlock,
  type OrgEventAutomationLogicJoin,
  type OrgEventAutomationSource,
  type ResourceIntersectCondition,
  formatSpatialMatchPhrase,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { normalizeWeatherServiceUrl } from './weatherFeatureServerCatalog';

export type AddAutomationConditionMode = 'compound' | 'independent';

export type AutomationRuleLogicSlice = {
  logic: OrgEventAutomationLogic | string;
  source: OrgEventAutomationSource;
  conditions: OrgEventAutomationCondition[];
  logicBlocks: OrgEventAutomationLogicBlock[];
};

export function newConditionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cond-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sanitizeLogicBlocks(
  blocks: OrgEventAutomationLogicBlock[]
): OrgEventAutomationLogicBlock[] {
  const sanitized: OrgEventAutomationLogicBlock[] = [];
  blocks.forEach((block, index) => {
    if (block.type === 'condition') {
      if (!block.conditionId) return;
      sanitized.push({
        type: 'condition',
        conditionId: block.conditionId,
        joinWithPrevious:
          index === 0 ? undefined : (block.joinWithPrevious ?? 'and'),
      });
      return;
    }
    if (block.conditionIds.length === 0) return;
    const internalJoin: OrgEventAutomationLogicJoin =
      block.internalJoin === 'or' ? 'or' : 'and';
    sanitized.push({
      type: 'group',
      id: block.id,
      joinWithPrevious:
        index === 0 ? undefined : (block.joinWithPrevious ?? 'and'),
      internalJoin,
      conditionIds: [...block.conditionIds],
    });
  });
  return sanitized;
}

export function ensureConditionIds(
  conditions: OrgEventAutomationCondition[]
): OrgEventAutomationCondition[] {
  return conditions.map((condition) =>
    condition.id ? condition : { ...condition, id: newConditionId() }
  );
}

export function getConditionById(
  conditions: OrgEventAutomationCondition[],
  conditionId: string
): OrgEventAutomationCondition | undefined {
  return conditions.find((c) => c.id === conditionId);
}

export function resolveConditionSource(
  condition: OrgEventAutomationCondition,
  ruleSource: OrgEventAutomationSource
): OrgEventAutomationSource {
  return condition.source ?? ruleSource;
}

export function normalizeAutomationRuleLogic<
  T extends AutomationRuleLogicSlice,
>(rule: T): T {
  const conditions = ensureConditionIds(
    Array.isArray(rule.conditions) ? rule.conditions : []
  );

  if (Array.isArray(rule.logicBlocks) && rule.logicBlocks.length > 0) {
    const knownIds = new Set(
      conditions.map((c) => c.id).filter((id): id is string => Boolean(id))
    );
    const logicBlocks = sanitizeLogicBlocks(
      rule.logicBlocks
        .map((block) => {
          if (block.type === 'condition') {
            return knownIds.has(block.conditionId) ? block : null;
          }
          const conditionIds = block.conditionIds.filter((id) =>
            knownIds.has(id)
          );
          if (conditionIds.length === 0) return null;
          return { ...block, conditionIds };
        })
        .filter((b): b is OrgEventAutomationLogicBlock => b != null)
    );

    return {
      ...rule,
      conditions,
      logicBlocks,
      logic: deriveLogicFromBlocks({ ...rule, conditions, logicBlocks }),
    };
  }

  if (conditions.length === 0) {
    return { ...rule, conditions, logicBlocks: [] };
  }

  const join: OrgEventAutomationLogicJoin = rule.logic === 'any' ? 'or' : 'and';

  const logicBlocks: OrgEventAutomationLogicBlock[] =
    conditions.length === 1
      ? [{ type: 'condition', conditionId: conditions[0].id as string }]
      : [
          {
            type: 'group',
            id: newConditionId(),
            internalJoin: join,
            conditionIds: conditions.map((c) => c.id as string),
          },
        ];

  return {
    ...rule,
    conditions,
    logicBlocks: sanitizeLogicBlocks(logicBlocks),
    logic: join === 'or' ? 'any' : 'all',
  };
}

export function deriveLogicFromBlocks(
  rule: Pick<AutomationRuleLogicSlice, 'logicBlocks' | 'logic'>
): OrgEventAutomationLogic {
  const blocks = rule.logicBlocks ?? [];
  const topLevelJoins = blocks
    .slice(1)
    .map((block) => block.joinWithPrevious ?? 'and');

  if (topLevelJoins.length === 0) {
    const onlyBlock = blocks[0];
    if (onlyBlock?.type === 'group') {
      return onlyBlock.internalJoin === 'or' ? 'any' : 'all';
    }
    return 'all';
  }

  if (topLevelJoins.every((j) => j === 'and')) return 'all';
  if (topLevelJoins.every((j) => j === 'or')) return 'any';
  return 'any';
}

function formatConditionShort(
  condition: OrgEventAutomationCondition,
  ruleSource: OrgEventAutomationSource
): string {
  const source = resolveConditionSource(condition, ruleSource);
  const feed =
    source.layerLabel?.trim() ||
    (isSensorAlphaSourcePreset(source)
      ? 'Sensor Alpha'
      : isTropicalSourcePreset(source)
        ? 'Tropical'
        : isArcGisLayerSourcePreset(source)
          ? 'ArcGIS layer'
          : source.serviceUrl?.trim() || 'ArcGIS');

  if (condition.kind === 'attribute') {
    const fieldLabel =
      source.fieldSnapshot?.alias?.trim() ||
      source.fieldSnapshot?.name ||
      condition.field;
    const op = condition.operator === '==' ? '=' : condition.operator;
    return `${feed}: ${fieldLabel} ${op} ${condition.value}`;
  }
  if (condition.kind === 'aor_intersect') {
    const selected = condition.aorIds?.length ?? 0;
    return `${feed}: ≥ ${condition.minCount} of ${selected} AOR${
      selected === 1 ? '' : 's'
    } ${formatSpatialMatchPhrase(condition.proximityKm)}`;
  }
  return `${feed}: ≥ ${condition.minCount} resource(s) ${formatSpatialMatchPhrase(
    condition.proximityKm
  )}`;
}

export function formatAutomationRuleFormula(
  rule: AutomationRuleLogicSlice
): string {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.logicBlocks.length === 0) {
    return 'Add at least one condition.';
  }

  const formatBlock = (block: OrgEventAutomationLogicBlock): string => {
    if (block.type === 'condition') {
      const condition = getConditionById(
        normalized.conditions,
        block.conditionId
      );
      return condition
        ? `[${formatConditionShort(condition, normalized.source)}]`
        : '[Unknown]';
    }
    const joiner = block.internalJoin === 'or' ? ' | ' : ' AND ';
    const inner = block.conditionIds
      .map((id) => {
        const condition = getConditionById(normalized.conditions, id);
        return condition
          ? formatConditionShort(condition, normalized.source)
          : 'Unknown';
      })
      .join(joiner);
    return `[${inner}]`;
  };

  return normalized.logicBlocks
    .map((block, index) => {
      const text = formatBlock(block);
      if (index === 0) return text;
      const join = block.joinWithPrevious ?? 'and';
      return `${join === 'and' ? ' AND ' : ' OR '}${text}`;
    })
    .join('');
}

/**
 * Prose counterpart to {@link formatAutomationRuleFormula}: same structure, but
 * lowercase "and"/"or" and no brackets, so it reads as a sentence. Parentheses
 * appear only where the nesting is genuinely ambiguous, meaning a
 * multi-condition group sitting alongside other top-level blocks.
 */
export function describeRuleConditions(rule: AutomationRuleLogicSlice): string {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.logicBlocks.length === 0) return '';

  const needsParens = normalized.logicBlocks.length > 1;
  const conditionText = (id: string): string => {
    const condition = getConditionById(normalized.conditions, id);
    return condition
      ? formatConditionShort(condition, normalized.source)
      : 'an unknown condition';
  };

  const describeBlock = (block: OrgEventAutomationLogicBlock): string => {
    if (block.type === 'condition') return conditionText(block.conditionId);
    const joiner = block.internalJoin === 'or' ? ' or ' : ' and ';
    const inner = block.conditionIds.map(conditionText).join(joiner);
    return needsParens && block.conditionIds.length > 1 ? `(${inner})` : inner;
  };

  return normalized.logicBlocks
    .map((block, index) => {
      const text = describeBlock(block);
      if (index === 0) return text;
      const join = (block.joinWithPrevious ?? 'and') === 'or' ? 'or' : 'and';
      return `${join} ${text}`;
    })
    .join(' ');
}

/** The optional follow-on actions, summarized for the reads-as sentence. */
export type RuleNarrativeActions = {
  eventName?: string;
  /** Channel labels already resolved for display, e.g. ['Email', 'In-app']. */
  notifyChannels?: string[];
  notifyRecipientCount?: number;
  checklistItemCount?: number;
};

/**
 * One sentence describing what the rule does end to end, for the pinned summary
 * above the stepper. Reads as "When <conditions>, create ... , notify ... , and
 * attach ...".
 */
export function describeRule(
  rule: AutomationRuleLogicSlice,
  actions: RuleNarrativeActions = {}
): string {
  const conditions = describeRuleConditions(rule);
  if (!conditions) {
    return 'Add a condition to describe when this rule should run.';
  }

  const clauses: string[] = [];
  const eventName = actions.eventName?.trim();
  clauses.push(
    eventName ? `create an event named "${eventName}"` : 'create an event'
  );

  const channels = actions.notifyChannels ?? [];
  if (channels.length > 0) {
    const recipients = actions.notifyRecipientCount ?? 0;
    const who =
      recipients > 0
        ? `${recipients} recipient${recipients === 1 ? '' : 's'}`
        : 'no one yet';
    clauses.push(`notify ${who} by ${formatList(channels)}`);
  }

  const items = actions.checklistItemCount ?? 0;
  if (items > 0) {
    clauses.push(`attach ${items} response task${items === 1 ? '' : 's'}`);
  }

  return `When ${conditions}, ${formatList(clauses)}.`;
}

function formatList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * A rule is compound when more than one condition feeds the formula: either
 * several top-level blocks joined by AND/OR, or one group joining its own
 * conditions.
 */
export function isCompoundRule(
  logicBlocks: OrgEventAutomationLogicBlock[]
): boolean {
  return (
    logicBlocks.length > 1 ||
    logicBlocks.some(
      (block) => block.type === 'group' && block.conditionIds.length > 1
    )
  );
}

export type FeedPreset =
  | 'tropical'
  | 'wildfire_incidents'
  | 'custom'
  | 'sensor_alpha'
  | 'arcgis_layer';

export function createDefaultCondition(
  preset: FeedPreset = 'custom'
): OrgEventAutomationCondition {
  const id = newConditionId();
  if (preset === 'tropical') {
    return {
      id,
      kind: 'resource_intersect',
      minCount: 1,
      geometryFrom: 'cone',
      source: { ...DEFAULT_ACTIVE_HURRICANES_SOURCE },
    };
  }
  if (preset === 'wildfire_incidents') {
    return {
      id,
      kind: 'resource_intersect',
      minCount: 1,
      geometryFrom: 'feature',
      source: { ...DEFAULT_WILDFIRE_INCIDENTS_SOURCE },
    };
  }
  if (preset === 'sensor_alpha') {
    return {
      id,
      kind: 'attribute',
      field: 'sensor_value',
      operator: '>=',
      value: '5',
      source: { ...DEFAULT_SENSOR_ALPHA_SOURCE },
    };
  }
  if (preset === 'arcgis_layer') {
    return {
      id,
      kind: 'attribute',
      field: '',
      operator: '==',
      value: '',
      source: { ...DEFAULT_ARCGIS_LAYER_SOURCE },
    };
  }
  return {
    id,
    kind: 'resource_intersect',
    minCount: 1,
    geometryFrom: 'feature',
    source: {
      type: 'arcgis_feature',
      preset: 'custom',
      serviceUrl: '',
      layerLabel: 'Custom ArcGIS layer',
    },
  };
}

export function addAutomationCondition(
  rule: AutomationRuleLogicSlice,
  options: {
    mode: AddAutomationConditionMode;
    join: OrgEventAutomationLogicJoin;
    preset?: FeedPreset;
  }
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const newCondition = createDefaultCondition(options.preset ?? 'custom');
  const conditions = [...normalized.conditions, newCondition];
  let logicBlocks = [...normalized.logicBlocks];

  if (logicBlocks.length === 0) {
    return {
      ...normalized,
      conditions,
      logicBlocks: [
        { type: 'condition', conditionId: newCondition.id as string },
      ],
      logic: 'all',
    };
  }

  const lastBlockIndex = logicBlocks.length - 1;
  const lastBlock = logicBlocks[lastBlockIndex];

  if (options.mode === 'independent') {
    logicBlocks.push({
      type: 'condition',
      conditionId: newCondition.id as string,
      joinWithPrevious: options.join,
    });
  } else if (lastBlock.type === 'group') {
    logicBlocks[lastBlockIndex] = {
      ...lastBlock,
      internalJoin: options.join,
      conditionIds: [...lastBlock.conditionIds, newCondition.id as string],
    };
  } else {
    logicBlocks[lastBlockIndex] = {
      type: 'group',
      id: newConditionId(),
      joinWithPrevious: lastBlock.joinWithPrevious,
      internalJoin: options.join,
      conditionIds: [lastBlock.conditionId, newCondition.id as string],
    };
  }

  logicBlocks = sanitizeLogicBlocks(logicBlocks);
  return {
    ...normalized,
    conditions,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function removeAutomationCondition(
  rule: AutomationRuleLogicSlice,
  conditionId: string
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  if (normalized.conditions.length <= 1) {
    return normalized;
  }

  const conditions = normalized.conditions.filter((c) => c.id !== conditionId);
  const logicBlocks = sanitizeLogicBlocks(
    normalized.logicBlocks.flatMap((block): OrgEventAutomationLogicBlock[] => {
      if (block.type === 'condition') {
        return block.conditionId === conditionId ? [] : [block];
      }
      if (!block.conditionIds.includes(conditionId)) {
        return [block];
      }
      const remainingIds = block.conditionIds.filter(
        (id) => id !== conditionId
      );
      if (remainingIds.length === 0) return [];
      if (remainingIds.length === 1) {
        return [
          {
            type: 'condition',
            conditionId: remainingIds[0],
            joinWithPrevious: block.joinWithPrevious,
          },
        ];
      }
      return [{ ...block, conditionIds: remainingIds }];
    })
  );

  return {
    ...normalized,
    conditions,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function updateAutomationCondition(
  rule: AutomationRuleLogicSlice,
  conditionId: string,
  updater: (
    condition: OrgEventAutomationCondition
  ) => OrgEventAutomationCondition
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  return {
    ...normalized,
    conditions: normalized.conditions.map((condition) =>
      condition.id === conditionId ? updater(condition) : condition
    ),
  };
}

export function updateLogicBlockJoin(
  rule: AutomationRuleLogicSlice,
  blockIndex: number,
  join: OrgEventAutomationLogicJoin
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const logicBlocks = sanitizeLogicBlocks(
    normalized.logicBlocks.map((block, index) =>
      index === blockIndex ? { ...block, joinWithPrevious: join } : block
    )
  );
  return {
    ...normalized,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

export function updateGroupInternalJoin(
  rule: AutomationRuleLogicSlice,
  groupId: string,
  join: OrgEventAutomationLogicJoin
): AutomationRuleLogicSlice {
  const normalized = normalizeAutomationRuleLogic(rule);
  const logicBlocks = normalized.logicBlocks.map((block) =>
    block.type === 'group' && block.id === groupId
      ? { ...block, internalJoin: join }
      : block
  );
  return {
    ...normalized,
    logicBlocks,
    logic: deriveLogicFromBlocks({ logicBlocks, logic: normalized.logic }),
  };
}

function isWildfireIncidentsSource(
  source: OrgEventAutomationSource | undefined
): boolean {
  if (!source) return false;
  if (source.preset === 'wildfire_incidents') return true;
  if (!source.serviceUrl) return false;
  return (
    normalizeWeatherServiceUrl(source.serviceUrl) ===
    normalizeWeatherServiceUrl(CURRENT_WILDFIRE_INCIDENTS_URL)
  );
}

export function feedPresetFromSource(
  source: OrgEventAutomationSource | undefined
): FeedPreset {
  if (!source) return 'custom';
  if (isSensorAlphaSourcePreset(source)) return 'sensor_alpha';
  if (isTropicalSourcePreset(source)) return 'tropical';
  if (isWildfireIncidentsSource(source)) return 'wildfire_incidents';
  if (isArcGisLayerSourcePreset(source)) return 'arcgis_layer';
  return 'custom';
}

export function sourceForPreset(
  preset: FeedPreset,
  existing?: OrgEventAutomationSource
): OrgEventAutomationSource {
  if (preset === 'tropical') return { ...DEFAULT_ACTIVE_HURRICANES_SOURCE };
  if (preset === 'wildfire_incidents') {
    return { ...DEFAULT_WILDFIRE_INCIDENTS_SOURCE };
  }
  if (preset === 'sensor_alpha') return { ...DEFAULT_SENSOR_ALPHA_SOURCE };
  if (preset === 'arcgis_layer') {
    if (existing?.preset === 'arcgis_layer') {
      return { ...existing };
    }
    return { ...DEFAULT_ARCGIS_LAYER_SOURCE };
  }
  return {
    type: 'arcgis_feature',
    preset: 'custom',
    serviceUrl: existing?.preset === 'custom' ? existing.serviceUrl || '' : '',
    layerLabel:
      existing?.preset === 'custom'
        ? existing.layerLabel || 'Custom ArcGIS layer'
        : 'Custom ArcGIS layer',
  };
}

export type { AttributeCondition, ResourceIntersectCondition };
